import React from 'react';
import { useQuery } from 'react-query';
import { PieChart, Pie, Cell, Legend } from 'recharts';

import Box from '@material-ui/core/Box';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import { CircularProgress } from '@material-ui/core';

import { colors } from '@picardy/picardy-design-system';

import { useGlobalAlert } from '../GlobalAlert';
import { getClassroomProgressForCourse } from '../../../api/educator';

interface Props {
  classroomId: string;
  courseId: string;
}

export default function ClassroomProgressChart({
  classroomId,
  courseId,
}: Props) {
  const { update: updateGlobalAlert } = useGlobalAlert();
  const { data, isLoading } = useQuery({
    queryKey: ['classroom', classroomId, 'courseProgress', courseId],
    queryFn: async () =>
      await getClassroomProgressForCourse(classroomId, courseId),
    onError: err => {
      console.error(err);
      updateGlobalAlert({
        severity: 'warning',
        content: "We couldn't load the statistics for your classroom.",
      });
    },
  });

  if (isLoading) return <CircularProgress />;
  if (!data) return <></>;

  const chartData = [{ name: 'Completed', value: data.completed }];

  if (data.completed !== data.total)
    chartData.push({
      name: 'Not completed',
      value: data.total - data.completed,
    });

  const chartColors = [colors.picardyBlue, colors.picardyRose];

  return (
    <Box style={{ minHeight: 248, width: '100%' }}>
      <Typography variant="h5">Classroom progress</Typography>
      <Link href={data.classroom.manageUrl}>{data.classroom.name}</Link>
      <PieChart height={220} width={300}>
        <Pie
          isAnimationActive={false}
          data={chartData}
          dataKey="value"
          nameKey="name"
          cy={90}
          cx={90}
        >
          {chartData.map((_entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={chartColors[index % chartColors.length]}
            />
          ))}
        </Pie>
        <Legend verticalAlign="bottom" align="left" />
      </PieChart>
    </Box>
  );
}
